import React, { useState } from 'react';
import { ComposedChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend, Bar } from 'recharts';
import { CalculationResult } from '../../types';
import { colorSystem } from '../../utils/colorSystem';
import { ChartToggle } from './ChartToggle';
import { formatAxisValue, formatTooltipValue } from '../../utils/formatters';

interface DailyProductivityTrendChartProps {
  results: CalculationResult[];
}

// Utility function to truncate Y-axis labels
const truncateLabel = (label: string, maxLength: number = 16): string => {
  if (label.length <= maxLength) return label;
  return label.substring(0, maxLength - 3) + '...';
};

export function DailyProductivityTrendChart({ results }: DailyProductivityTrendChartProps) {
  const [showSecondaryAxis, setShowSecondaryAxis] = useState(true);
  const [showTheoreticalProductivity, setShowTheoreticalProductivity] = useState(false);
  const isKnownExperiencesMode = false;

  const chartData = React.useMemo(() => {
    colorSystem.reset();
    
    const dataMap = new Map<string, { [key: string]: any }>();
    
    results.forEach(result => {
      const date = result.calculationDate;
      if (!dataMap.has(date)) {
        dataMap.set(date, {
          date,
          ept: result.ept,
          theoreticalProductivityPercentage: result.theoreticalProductivityPercentage,
          values: {}
        });
      }
      const roleKey = `${result.role}-${result.experience}`;
      const entry = dataMap.get(date)!;
      entry.values[`${roleKey}-linear`] = (result.linearTimeImpactSecondsPerDay || 0) / 3600; // Convert to hours
      entry.values[`${roleKey}-exponential`] = (result.exponentialTimeImpactSecondsPerDay || 0) / 3600; // Convert to hours
    });

    const data = Array.from(dataMap.entries())
      .map(([date, values]) => ({
        date,
        ...values
      }))
      .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());

    const roleKeys = Array.from(new Set(
      results.map(r => `${r.role}-${r.experience}`)
    ));
    const lineTypes = ['linear', 'exponential'];

    return { data, roleKeys, lineTypes };
  }, [results]);

  const CustomTooltip = ({ active, payload, label }: any) => {
    if (!active || !payload || !payload.length) return null;

    return (
      <div className="bg-white p-3 border border-gray-200 rounded-lg shadow-lg">
        <p className="font-medium text-gray-900 mb-2">
          {new Date(label).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric'
          })}
        </p>
        {payload.map((entry: any) => {
          if (entry.dataKey === 'theoreticalProductivityPercentage') {
            return (
              <div key={entry.dataKey} className="flex items-center gap-2 text-sm">
                <div
                  className="w-3 h-3 rounded-full"
                  style={{ backgroundColor: entry.color }}
                />
                <span className="text-gray-700">Theoretical Productivity:</span>
                <span className="font-medium">{formatTooltipValue(entry.value, '', '%')}</span>
              </div>
            );
          }
          if (entry.dataKey === 'ept') {
            return (
              <div key={entry.dataKey} className="flex items-center gap-2 text-sm">
                <div
                  className="w-3 h-3 rounded-full"
                  style={{ backgroundColor: entry.color }}
                />
                <span className="text-gray-700">EPT:</span>
                <span className="font-medium">{formatTooltipValue(entry.value, '', 's')}</span>
              </div>
            );
          }

          const value = entry.value;
          const isNegative = value < 0;
          const displayValue = Math.abs(value);
          const impactType = entry.dataKey.includes('linear') ? 'Linear' : 'Exponential';

          return (
            <div key={entry.dataKey} className="flex items-center gap-2 text-sm">
              <div
                className="w-3 h-3 rounded-full"
                style={{ backgroundColor: entry.color }}
              />
              <span className="text-gray-700">{impactType}:</span>
              <span 
                className="font-medium"
                style={{ color: isNegative ? '#00FF00' : '#FF0000' }}
              >
                {isNegative ? '-' : '+'}{formatTooltipValue(displayValue, '', ' hours/day')}
              </span>
            </div>
          );
        })}
      </div>
    );
  };

  return (
    <div className="w-full h-[600px] bg-white p-6 rounded-xl shadow-sm chart-container">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h3 className="text-xl font-semibold text-gray-900">Daily Productivity Impact</h3>
          <p className="text-sm text-gray-500">
            Daily linear and exponential time impact from EPT over time
          </p>
        </div>
        <ChartToggle
          showSecondaryAxis={showTheoreticalProductivity}
          onToggle={setShowTheoreticalProductivity}
          label="Show Theoretical Productivity"
        />
        {!showTheoreticalProductivity && (
          <ChartToggle
            showSecondaryAxis={showSecondaryAxis}
            onToggle={setShowSecondaryAxis}
            label="Show EPT"
          />
        )}
      </div>
      
      <ResponsiveContainer width="100%" height="87%">
        <ComposedChart 
          data={chartData.data}
          margin={{ top: 60, right: 30, left: 20, bottom: 60 }}
        >
          <defs>
            <linearGradient id="eptGradient" x1="0" y1="1" x2="0" y2="0">
              <stop offset="0%" stopColor="#F59E0B" stopOpacity={0.3}/>
              <stop offset="100%" stopColor="#D97706" stopOpacity={0.8}/>
            </linearGradient>
          </defs>
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis
            dataKey="date"
            label={{ 
              value: 'Analysis Date', 
              position: 'bottom', 
              offset: 40,
              style: { fontWeight: 'bold' }
            }}
            tick={{ dy: 10 }}
          />
          <YAxis
            yAxisId="left"
            width={100}
            label={{ 
              value: truncateLabel('Hours Impact/Day'), 
              angle: 0,
              position: 'top',
              dy: -30,
              textAnchor: 'start',
              dx: 50,
              style: { fontWeight: 'bold' }
            }}
            tickFormatter={formatAxisValue}
          />
          {showSecondaryAxis && (
            <YAxis
              yAxisId="right"
              orientation="right"
              width={100}
              domain={showTheoreticalProductivity ? [0, 100] : [0, 4]}
              label={{
                value: truncateLabel(showTheoreticalProductivity ? 'Theoretical Productivity (%)' : 'EPT (seconds)'),
                angle: 0,
                position: 'top',
                dy: -30,
                textAnchor: 'end',
                dx: -50,
                style: { fontWeight: 'bold' }
              }}
              tickFormatter={formatAxisValue}
            />
          )}
          <Tooltip content={<CustomTooltip />} />
          <Legend 
            verticalAlign="bottom" 
            height={36}
            wrapperStyle={{
              paddingTop: '20px',
              marginBottom: '-45px'
            }}
          />
          {chartData.roleKeys.map(roleKey => {
            const [role, experience] = roleKey.split('-');
            return chartData.lineTypes.map(lineType => {
              const lineColors = {
                'linear': '#3B82F6',
                'exponential': '#EF4444'
              };
              const lineName = isKnownExperiencesMode ? 
                `User's Experience Impact (${lineType})` : 
                `${roleKey} (${lineType})`;
                
              return (
                <Line
                  key={`${roleKey}-${lineType}`}
                  yAxisId="left"
                  type="monotone"
                  dataKey={`values.${roleKey}-${lineType}`}
                  name={lineName}
                  stroke={lineColors[lineType as keyof typeof lineColors]}
                  strokeWidth={2}
                  dot={{ r: 4 }}
                  activeDot={{ r: 6 }}
                  connectNulls={true}
                  strokeDashArray={lineType === 'exponential' ? '5,5' : '0'}
                />
              );
            });
          }).flat()}
          {showSecondaryAxis && showTheoreticalProductivity && (
            <Line
              yAxisId="right"
              type="monotone"
              dataKey="theoreticalProductivityPercentage"
              name="Theoretical Productivity"
              stroke="#9333EA"
              strokeWidth={2}
              dot={{ r: 4 }}
              activeDot={{ r: 6 }}
              connectNulls={true}
            />
          )}
          {showSecondaryAxis && !showTheoreticalProductivity && (
            <Bar
              yAxisId="right"
              dataKey="ept"
              name="EPT"
              fill="url(#eptGradient)"
              opacity={0.6}
              barSize={20}
            />
          )}
        </ComposedChart>
      </ResponsiveContainer>
    </div>
  );
}